import { useAuth } from "@/App";
import { Button } from "@/components/ui/button";
import { Star, Award, Users, ChefHat, MapPin, Trophy, Utensils, Wine } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useEffect } from "react";

const Landing = () => {
  const { user, login, loading } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user && !loading) {
      navigate('/dashboard');
    }
  }, [user, loading, navigate]);

  const features = [
    {
      icon: Star,
      title: "Track Your Stars",
      description: "Log every Michelin-starred restaurant visit and watch your star collection grow."
    },
    {
      icon: Award,
      title: "Earn Badges",
      description: "From Food Novice to Royal Foody - unlock prestigious badges as you dine."
    },
    {
      icon: Users,
      title: "Compete & Share",
      description: "Climb the leaderboard and share your culinary achievements with friends."
    },
    {
      icon: ChefHat,
      title: "Restaurant Perks",
      description: "Share your preferences and receive personalized treats from restaurants."
    }
  ];

  const badges = [
    { name: "Food Novice", stars: "0+", color: "text-zinc-400" },
    { name: "Food Explorer", stars: "5+", color: "text-emerald-400" },
    { name: "Food Connoisseur", stars: "15+", color: "text-blue-400" },
    { name: "Prince Foody", stars: "30+", color: "text-purple-400" },
    { name: "Royal Foody", stars: "50+", color: "text-amber-400" }
  ];

  return (
    <div className="min-h-screen bg-background" data-testid="landing-page">
      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center overflow-hidden">
        {/* Background */}
        <div 
          className="absolute inset-0 bg-cover bg-center opacity-15"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1514362545857-3bc16549766b?q=80&w=2670&auto=format&fit=crop')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-background/80 to-background" />
        
        {/* Content */}
        <div className="relative z-10 text-center px-6 max-w-4xl mx-auto">
          <p className="text-xs uppercase tracking-[0.3em] text-primary mb-6 animate-fade-in">
            Your Culinary Identity
          </p>
          
          <h1 className="text-5xl sm:text-6xl lg:text-7xl font-bold tracking-tight mb-6 animate-fade-in stagger-1">
            <span className="gradient-text">Étoile</span>
          </h1>
          
          <p className="text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-4 animate-fade-in stagger-2 font-light">
            Tastes are different, lifestyles are different, and emotions and preferences are different.
          </p>
          
          <p className="text-base text-muted-foreground max-w-xl mx-auto mb-12 animate-fade-in stagger-2 font-light">
            Describe your own style and find a community you can identify with.
            Exchange ideas, connect with others, and enjoy the moment.
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center animate-fade-in stagger-3">
            <Button 
              onClick={() => navigate('/auth')}
              className="h-12 px-8 text-xs uppercase tracking-widest bg-primary text-primary-foreground hover:bg-primary/90 transition-all duration-300"
              data-testid="login-button"
            >
              Begin Your Journey
            </Button>
            <Button 
              variant="outline"
              onClick={() => document.getElementById('features').scrollIntoView({ behavior: 'smooth' })}
              className="h-12 px-8 text-xs uppercase tracking-widest border-border hover:border-primary hover:text-primary"
              data-testid="learn-more-button"
            >
              Discover More
            </Button>
          </div>

          {/* Star decoration */}
          <div className="flex justify-center gap-2 mt-16 animate-fade-in stagger-4">
            {[1, 2, 3].map((i) => (
              <Star 
                key={i} 
                className="w-5 h-5 text-primary fill-primary star-glow" 
                style={{ animationDelay: `${i * 0.2}s` }}
              />
            ))}
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
          <div className="w-6 h-10 border border-border rounded-full flex justify-center pt-2">
            <div className="w-1 h-2 bg-primary rounded-full" />
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-24 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">Why Étoile</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Your Culinary Legacy Awaits
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, index) => (
              <div 
                key={feature.title}
                className="group p-8 bg-card border border-border card-hover"
                style={{ animationDelay: `${index * 0.1}s` }}
                data-testid={`feature-card-${index}`}
              >
                <feature.icon className="w-8 h-8 text-primary mb-6 group-hover:scale-110 transition-transform" />
                <h3 className="text-lg font-semibold mb-3">{feature.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Badge System Section */}
      <section className="py-24 px-6 bg-card/50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <p className="text-xs uppercase tracking-[0.3em] text-primary mb-4">Badge System</p>
            <h2 className="text-3xl sm:text-4xl font-bold tracking-tight">
              Rise Through The Ranks
            </h2>
          </div>

          <div className="flex flex-wrap justify-center gap-6">
            {badges.map((badge, index) => (
              <div 
                key={badge.name}
                className="flex flex-col items-center p-6 min-w-[160px]"
                data-testid={`badge-${index}`}
              >
                <div className={`w-16 h-16 rounded-full border-2 ${badge.color} border-current flex items-center justify-center mb-4`}>
                  <Trophy className={`w-7 h-7 ${badge.color}`} />
                </div>
                <h4 className={`font-semibold ${badge.color} mb-1`}>{badge.name}</h4>
                <p className="text-xs text-muted-foreground">{badge.stars} stars</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-24 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8 text-center">
            <div data-testid="stat-restaurants">
              <Utensils className="w-6 h-6 text-primary mx-auto mb-3" />
              <p className="text-3xl font-bold mb-1">2,500+</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Michelin Restaurants</p>
            </div>
            <div data-testid="stat-foodies">
              <Users className="w-6 h-6 text-primary mx-auto mb-3" />
              <p className="text-3xl font-bold mb-1">10K+</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Foodies</p>
            </div>
            <div data-testid="stat-countries">
              <MapPin className="w-6 h-6 text-primary mx-auto mb-3" />
              <p className="text-3xl font-bold mb-1">30+</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Countries</p>
            </div>
            <div data-testid="stat-stars">
              <Star className="w-6 h-6 text-primary mx-auto mb-3" />
              <p className="text-3xl font-bold mb-1">50K+</p>
              <p className="text-xs text-muted-foreground uppercase tracking-wider">Stars Collected</p>
            </div>
          </div>
        </div>
      </section>

      {/* Restaurant Portal CTA */}
      <section className="py-24 px-6 bg-card/50">
        <div className="max-w-3xl mx-auto text-center">
          <Wine className="w-12 h-12 text-primary mx-auto mb-6" />
          <h2 className="text-3xl sm:text-4xl font-bold tracking-tight mb-6">
            For Restaurants
          </h2>
          <p className="text-muted-foreground mb-8 max-w-xl mx-auto">
            Access guest preferences to deliver personalized experiences. 
            Welcome your finest diners with complimentary treats.
          </p>
          <Button 
            variant="outline"
            onClick={() => navigate('/portal')}
            className="h-12 px-8 text-xs uppercase tracking-widest border-border hover:border-primary hover:text-primary"
            data-testid="restaurant-portal-button"
          >
            Restaurant Portal
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border">
        <div className="max-w-6xl mx-auto flex flex-col md:flex-row justify-between items-center gap-4">
          <div className="flex items-center gap-2">
            <Star className="w-5 h-5 text-primary fill-primary" />
            <span className="font-semibold gradient-text">Étoile</span>
          </div>
          <div className="flex items-center gap-6">
            <a href="/privacy" className="text-xs text-muted-foreground hover:text-primary transition-colors">
              Privacy Policy
            </a>
            <a href="/terms" className="text-xs text-muted-foreground hover:text-primary transition-colors">
              Terms of Service
            </a>
          </div>
          <p className="text-xs text-muted-foreground">
            © 2026 Étoile. The art of fine dining, tracked.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
